'use client';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { ApiAttribute } from '@context/openapi/types';
import InputDescription from '@components/ui/DynamicForm/InputDescription';
import { type DateRange } from 'react-day-picker';
import { Calendar } from '@components/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/shadcn/popover';
import { cn } from '@components/lib/utils';
import { CalendarIcon } from 'lucide-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface DateRangeInputProps {
  field: ApiAttribute;
  onChange: (field: ApiAttribute, value: string) => void;
  onHelpVisible: (visible: boolean) => void;
  disabled?: boolean;
}

const DateRangeInput: React.FC<DateRangeInputProps> = ({
  field,
  onChange,
  onHelpVisible,
  disabled = false,
}) => {
  const [_isHelpVisible, setIsHelpVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(
    undefined
  );

  const getDateFromTimestamp = useCallback(
    (value: string | number | undefined | null): Date | null => {
      if (!value) return null;

      const numValue = Number(value);
      if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
        return null;
      }

      try {
        const date = new Date(numValue * 1000);
        if (isNaN(date.getTime())) {
          return null;
        }
        return date;
      } catch (_error) {
        return null;
      }
    },
    []
  );

  const parseValue = useCallback(
    (value: string): DateRange | undefined => {
      if (!value) return undefined;

      // Check if value is in range format [timestamp1,timestamp2]
      const isRangeFormat = /^\[.*\]$/.test(value.trim());
      if (!isRangeFormat) {
        return undefined;
      }

      try {
        const cleaned = value.replace(/^\[|\]$/g, '').replace(/"/g, '');
        const parts = cleaned
          .split(',')
          .map((p) => p.trim())
          .filter((p) => p);

        if (parts.length >= 2) {
          const from = getDateFromTimestamp(parts[0]);
          const to = getDateFromTimestamp(parts[1]);

          if (from && to) {
            return { from, to };
          }
        }
        return undefined;
      } catch (_error) {
        return undefined;
      }
    },
    [getDateFromTimestamp]
  );

  useEffect(() => {
    const parsed = parseValue(field.value as string);
    setDateRange(parsed);
    setPendingRange(parsed);

    if (field.value && !/^\[.*\]$/.test((field.value as string).trim())) {
      onChange(field, '');
    }
  }, [field.value, parseValue, onChange, field]);

  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('wheel', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('wheel', handleScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && pendingRange && (!pendingRange.from || !pendingRange.to)) {
      setPendingRange(dateRange);
    }
  }, [isOpen, pendingRange, dateRange]);

  const toUnixTime = useCallback((date: Date): number => {
    return Math.floor(date.getTime() / 1000);
  }, []);

  const isSameDay = useCallback((date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }, []);

  const handleDateChange = useCallback(
    (range: DateRange | undefined) => {
      setPendingRange(range);

      if (range?.from && range?.to) {
        if (!isSameDay(range.from, range.to)) {
          const startTimestamp = toUnixTime(range.from);
          const endTimestamp = toUnixTime(range.to);
          onChange(field, `[${startTimestamp},${endTimestamp}]`);
          setDateRange(range);
        }
      } else if (!range?.from && !range?.to) {
        onChange(field, '');
        setDateRange(undefined);
      }
    },
    [field, onChange, toUnixTime, isSameDay]
  );

  const displayValue = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return `${dayjs(dateRange.from).format('YYYY/MM/DD')}-${dayjs(dateRange.to).format('YYYY/MM/DD')}`;
    } else if (dateRange?.from) {
      return `${dayjs(dateRange.from).format('YYYY/MM/DD')}-...`;
    }
    return 'Range: yyyy/mm/dd';
  }, [dateRange]);

  return (
    <div className="ml-auto relative overflow-visible flex items-center gap-1 justify-between w-full z-10 opacity-100">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'pl-2 pr-2 py-0.5 border-l border-form-border',
              'bg-white text-left cursor-pointer truncate',
              'w-full min-w-0 flex items-center gap-1',
              !dateRange?.from && !dateRange?.to
                ? 'text-xs text-neutral-400 py-1'
                : 'text-sm',
              'focus-visible:outline-shade-aqua-300'
            )}
            data-pendo-mask
            disabled={disabled}
          >
            <CalendarIcon className="h-3 w-3 shrink-0 text-neutral-400" />
            <span className="truncate">{displayValue}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={pendingRange?.from || dateRange?.from}
            selected={pendingRange}
            onSelect={handleDateChange}
            className="rounded-lg border shadow-sm"
          />
        </PopoverContent>
      </Popover>
      <InputDescription
        description={field.description}
        onHelpVisible={(visible) => {
          setIsHelpVisible(visible);
          onHelpVisible(visible);
        }}
      />
    </div>
  );
};

export default DateRangeInput;
